import { SUBSCRIPTION_API_URL } from '@portal/config/base';
import { showNotification } from '@portal/shared/ui/Notification';
import { useQuery } from '@tanstack/react-query';
import { useSessionValue } from 'appLayer/auth';
import { useTranslation } from 'react-i18next';
import { useInternalApiRequest } from 'shared/hooks';
import { formatSubscriptionData } from 'shared/utils/subscriptionV2Helpers';
import { useStoreValue } from 'state/hooks/useStore';
import { getQueryKeyForInternal } from 'state/utils/getQueryKeyForInternal';
import { requestErrors } from '../constants';
import type { SubscriptionDetailsV2Response } from './types';

export const queryKeyForSubscriptionDetailsV2 = (store: string, contractId: number) =>
  getQueryKeyForInternal({
    keys: ['subscriptions', store, contractId]
  });

export const useQueryGetSubscriptionDetailsV2 = (contractId: number) => {
  const request = useInternalApiRequest();
  const store = useStoreValue();
  const { t } = useTranslation('subscriptionDetails');
  const session = useSessionValue();

  const data = useQuery({
    enabled: !!contractId && contractId > 0 && Boolean(session?.accessToken),
    queryFn: async () => {
      const apiUrl = SUBSCRIPTION_API_URL[store];
      if (!apiUrl) throw new Error(requestErrors.API_URL_NOT_DEFINED);

      const subscriptionData = await request<SubscriptionDetailsV2Response>({
        method: 'GET',
        url: `${apiUrl}/user/subscription/${contractId}`,
        shouldTimeout: true
      });

      if (!subscriptionData) {
        showNotification('mutationError', {
          title: t('v2.errors.apiError')
        });

        throw new Error(requestErrors.SUBSCRIPTION_DATA_NOT_FOUND);
      }

      return formatSubscriptionData(subscriptionData);
    },
    queryKey: queryKeyForSubscriptionDetailsV2(store, contractId),
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true
  });

  return {
    ...data,
    isError: data.isError && !(data.failureCount === 0)
  };
};
